"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAppDispatch, useAppSelector, useAppStore } from "@/store";
import { setCurrentUser } from "@/store/currentUserSlice";
import {
  addMessage,
  addRoom,
  addThreadMessage,
  clearUnreadCount,
  incrementUnreadCount,
  markMessagesAsRead,
  removeRoom,
  setMessages,
  setOnlineUsers,
  setRoomManuallyMarkedUnread,
  setRooms,
  setTypingUser,
  setUnreadCounts,
  setUnreadCountsTrusted,
  patchMessage,
  updateMessageReaction,
  updateMessageReplyCount,
  updateRoom,
  toggleMessagePin,
  addUnreadThread,
} from "@/store/features/chatSlice";
import {
  ChatNotification,
  Message,
  MessageEventData,
  MessageReadEventData,
  ReactionEventData,
  Room,
  ThreadReplyEventData,
  TypingEventData,
  UnreadCountData,
  PinEventData,
} from "../components/types";
import { isRoomMember } from "../utils/roomManagement";
import { User } from "@/types";
import { useDisplayNames } from "@/context/display-names-context";

// Singleton to manage socket instances across multiple hook calls
const socketManager = {
  socket: null as Socket | null,
  token: null as string | null,
  agencyId: null as number | null,
  refCount: 0,
  reconnectAttempt: 0,
  isReconnecting: false,
  eventsBound: false,
  // True once the first `unreadCounts` push has been received on the current
  // socket connection. Reset on every new socket creation and on each
  // `connect`/reconnect. The first push is trusted verbatim (handshake =
  // server is the only source of truth); subsequent pushes go through the
  // ignore-zero / grace-period logic in the reducer.
  initialUnreadCountsReceived: false,
};

export const useSocket = (token: string | null) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  // Reactive agency-id: when the user switches agency, this changes and forces
  // initializeSocket to re-run, which in turn recreates the socket with the
  // new scope (server-side `socket.user.agency` is otherwise frozen at
  // connection time).
  const activeAgencyIdSelected = useAppSelector(
    (s) => s.user?.currentUser?.agency?.id ?? null,
  );
  const displayNames = useDisplayNames();
  const displayNamesRef = useRef(displayNames);
  displayNamesRef.current = displayNames;
  const STRAPI_URL =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:1338";

  const markedMessagesRef = useRef<Set<number>>(new Set());
  const lastMarkedTimeRef = useRef<{ [roomId: number]: number }>({});
  const pendingMarkRequestRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMessagesRef = useRef<number[]>([]);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Ref to track active typing indicators with auto-expiration
  const activeTypingRef = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Helper to check if a user has read a message, handling both object and ID relationships
  const hasUserReadMessage = useCallback(
    (message: Message, userId: number): boolean => {
      if (!message.readBy) return false;

      const readByArray = Array.isArray(message.readBy) ? message.readBy : [];
      return readByArray.some((reader: number | { id: number }) =>
        typeof reader === "object" ? reader.id === userId : reader === userId,
      );
    },
    [],
  );

  // Debounced function to mark messages as read via API
  const processMarkedMessages = useCallback(() => {
    if (pendingMessagesRef.current.length === 0 || !token) return;

    const messageIds = [...pendingMessagesRef.current];
    pendingMessagesRef.current = [];

    fetch(`${STRAPI_URL}/api/messages/mark-read`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ messageIds }),
    }).catch((err) =>
      console.error("Error marking messages as read via API:", err),
    );
  }, [token, STRAPI_URL]);

  // Main function to mark messages as read
  const markRoomMessagesAsRead = useCallback(
    (roomId: number, userId: number) => {
      const socket = socketManager.socket;
      if (!socket || !roomId || !userId) {
        console.warn(
          "Cannot mark messages as read: missing socket, roomId, or userId",
        );
        return;
      }

      const state = store.getState();
      const messages = state.chat?.messages || [];

      // Throttle with a 1-second cooldown per room
      const now = Date.now();
      const lastMarked = lastMarkedTimeRef.current[roomId] || 0;
      if (now - lastMarked < 1000) {
        return;
      }
      lastMarkedTimeRef.current[roomId] = now;

      // Find unread messages in this room for this user
      const unreadMessages = messages.filter(
        (msg) =>
          (typeof msg.room === "object" ? msg.room.id : msg.room) === roomId &&
          (typeof msg.sender === "object" ? msg.sender.id : msg.sender) !==
            userId &&
          !hasUserReadMessage(msg, userId) &&
          !markedMessagesRef.current.has(msg.id),
      );

      if (unreadMessages.length === 0) {
        return;
      }

      const messageIds = unreadMessages.map((msg) => msg.id);

      // Track these messages as marked
      messageIds.forEach((id) => markedMessagesRef.current.add(id));

      // Emit socket event first for immediate UI update
      socket.emit("markMessagesRead", { messageIds, roomId, userId });

      // Dispatch Redux action to update UI immediately
      dispatch(markMessagesAsRead({ messageIds, userId }));
      dispatch(clearUnreadCount(roomId));

      // Add to pending messages for batch API call
      pendingMessagesRef.current = [
        ...pendingMessagesRef.current,
        ...messageIds,
      ];

      // Clear any existing timeout
      if (pendingMarkRequestRef.current) {
        clearTimeout(pendingMarkRequestRef.current);
      }

      // Set up debounced API call (500ms delay)
      pendingMarkRequestRef.current = setTimeout(() => {
        processMarkedMessages();
      }, 500);
    },
    [dispatch, hasUserReadMessage, processMarkedMessages, store],
  );

  // Send a chat message with optimistic update
  const sendMessage = useCallback(
    (content: string, roomId: number, messageType: string = "text") => {
      if (!socketManager.socket || !socketManager.socket.connected) {
        console.error("Socket is not connected - message not sent");
        // Show error to user
        return;
      }

      const state = store.getState();
      const currentUser = state.user?.currentUser;

      if (!currentUser) {
        console.error("No current user found");
        return;
      }

      // Find the room data for the optimistic update
      const roomData = state.chat?.rooms.find((r) => r.id === roomId);

      // Create optimistic message (temporary ID with negative number)
      const optimisticMessage: Message = {
        id: -Date.now(), // Temporary negative ID
        content,
        sender: currentUser as User,
        room: roomData || ({ id: roomId } as Room),
        messageType: messageType as 'text' | 'audio' | 'deleted',
        readBy: [currentUser.id],
        createdAt: new Date().toISOString(),
        attachments: [],
      };

      // Add optimistic message to UI immediately
      dispatch(addMessage(optimisticMessage));

      // Send the actual message via socket
      socketManager.socket.emit("sendMessage", {
        content,
        roomId,
        senderId: currentUser.id,
        messageType,
      });
    },
    [store, dispatch],
  );

  // Join a room
  const joinRoom = useCallback((roomId: number) => {
    if (!socketManager.socket || !socketManager.socket.connected) {
      console.warn("Cannot join room: socket not connected");
      return;
    }
    socketManager.socket.emit("joinRoom", roomId);
  }, []);

  // Leave a room
  const leaveRoom = useCallback((roomId: number) => {
    if (!socketManager.socket || !socketManager.socket.connected) {
      console.warn("Cannot leave room: socket not connected");
      return;
    }
    socketManager.socket.emit("leaveRoom", roomId);
  }, []);

  // Send typing indicator (aliased as emitTypingEvent for compatibility)
  const emitTypingEvent = useCallback(
    (roomId: number, isTyping: boolean) => {
      if (!socketManager.socket || !socketManager.socket.connected) {
        return;
      }
      socketManager.socket.emit("typing", { roomId, isTyping });
    },
    [],
  );

  // Get unread message counts
  const getUnreadCounts = useCallback(() => {
    if (!socketManager.socket || !socketManager.socket.connected) {
      console.warn("Cannot get unread counts: socket not connected");
      return;
    }
    socketManager.socket.emit("getUnreadCounts");
  }, []);

  // Handle chat notifications (internal use)
  const handleChatNotification = useCallback((notification: ChatNotification) => {
    setNotifications((prev) => [...prev, notification]);

    // If your app has a notification system, you could trigger it here
    if (window.Notification && Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.message,
      });
    }

    // You might also want to play a sound
    // const audio = new Audio('/notification-sound.mp3');
    // audio.play().catch(e => console.error('Error playing notification sound:', e));
  }, []);

  // Initialize or reinitialize socket connection
  const initializeSocket = useCallback(() => {
    if (!token) {
      // If no token, disconnect existing socket
      if (socketManager.socket) {
        console.log("[Socket] Disconnecting due to no token");
        socketManager.socket.disconnect();
        socketManager.socket = null;
        socketManager.token = null;
        socketManager.agencyId = null;
        setIsConnected(false);
      }
      return null;
    }

    // Token OR active-agency change → recreate socket. Agency matters because
    // after an in-place switch the JWT stays the same but `socket.user.agency`
    // on the server is fixed at connection time, so without a recreate the
    // server keeps pushing unread-counts for the previous scope.
    const activeAgencyId = store.getState().user?.currentUser?.agency?.id ?? null;
    if (socketManager.token !== token || socketManager.agencyId !== activeAgencyId) {
      console.log(
        `[Socket] ${socketManager.token !== token ? "token" : "agency"} changed, recreating socket connection`,
      );

      // Disconnect old socket if exists
      if (socketManager.socket) {
        socketManager.socket.removeAllListeners();
        socketManager.socket.disconnect();
        socketManager.socket = null;
        socketManager.eventsBound = false;
      }

      // Reset reconnection tracking
      socketManager.reconnectAttempt = 0;
      socketManager.isReconnecting = false;
      socketManager.token = token;
      socketManager.agencyId = activeAgencyId;

      // Create new socket with updated token
      const socketInstance = io(STRAPI_URL, {
        auth: { token, agencyId: activeAgencyId || null },
        // Multi-replica backend: websocket first, polling fallback + sticky cookie.
        transports: ["websocket", "polling"],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      socketManager.socket = socketInstance;
      socketManager.eventsBound = true;
      // Fresh socket → we haven't received any unread-counts pushes on it yet.
      socketManager.initialUnreadCountsReceived = false;
      // Expose for consumers that subscribe outside the hook (e.g. TicketChatThread,
      // support Kanban). They only read `window.__socket` in useEffect mount.
      if (typeof window !== "undefined") {
        (window as unknown as { __socket: Socket | null }).__socket = socketInstance;
      }

      // Set up event listeners
      socketInstance.on("connect", () => {
        console.log("[Socket] Connected successfully");
        setIsConnected(true);
        socketManager.reconnectAttempt = 0;
        socketManager.isReconnecting = false;
        // Every reconnect is a fresh handshake: accept the next unread-counts
        // push as ground truth.
        socketManager.initialUnreadCountsReceived = false;

        const state = store.getState();
        const currentRoomId = state.chat?.selectedRoomId;
        const currentUserId = state.user?.currentUser?.id;

        if (currentRoomId && currentUserId) {
          socketInstance.emit("joinRoom", currentRoomId);
          // Support_bot rooms: the server is authoritative for mark-read
          // (REST auto-mark in getRoomMessages / getThreadMessages + the explicit
          // socket markMessagesRead event fired by the room UI on engage).
          // Skipping this preemptive client-side mark avoids zeroing out an
          // unread count the server just pushed us.
          const currentRoom = state.chat?.rooms.find((r) => r.id === currentRoomId);
          const currentRoomIsSupportBot = currentRoom?.room_kind === 'support_bot';
          if (!currentRoomIsSupportBot) {
            setTimeout(() => {
              markRoomMessagesAsRead(currentRoomId, currentUserId);
            }, 1000);
          }
        }
      });

      socketInstance.on("disconnect", (reason) => {
        console.log("[Socket] Disconnected:", reason);
        setIsConnected(false);

        // Handle auth-related disconnections
        if (reason === "io server disconnect") {
          // Server forcefully disconnected, likely auth issue
          console.error("[Socket] Server disconnected - likely auth failure");
          socketManager.token = null; // Clear token to force re-auth
        }
      });

      socketInstance.on("connect_error", (error: Error) => {
        console.error("[Socket] Connection error:", error.message);

        // Handle authentication errors
        if (error.message?.includes("401") || error.message?.includes("Unauthorized")) {
          console.error("[Socket] Authentication failed, clearing token");
          socketManager.token = null;
          socketManager.socket = null;
          socketInstance.disconnect();
          setIsConnected(false);

          // You might want to trigger a token refresh here
          // dispatch(refreshAuthToken());
        } else if (!socketManager.isReconnecting) {
          socketManager.isReconnecting = true;
          socketManager.reconnectAttempt++;

          if (socketManager.reconnectAttempt > 10) {
            console.error("[Socket] Max reconnection attempts reached");
            socketInstance.disconnect();
            socketManager.socket = null;
            socketManager.token = null;
          }
        }
      });

      socketInstance.on("reconnect", (attemptNumber) => {
        console.log("[Socket] Reconnected after", attemptNumber, "attempts");
        socketManager.reconnectAttempt = 0;
        socketManager.isReconnecting = false;
      });

      socketInstance.on("onlineUsers", (users: number[]) => {
        dispatch(setOnlineUsers(users));
      });

      // Session management events from backend
      socketInstance.on("session-update", () => {
        console.log("[Socket] Session update received, refreshing user data");
        fetch('/api/auth/current-user?force=true')
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.data) {
              dispatch(setCurrentUser(data.data));
            }
          })
          .catch(err => console.error('[Socket] Failed to refresh session:', err));
      });

      socketInstance.on("force-logout", (data: { reason?: string }) => {
        console.warn("[Socket] Force logout received, reason:", data?.reason);
        socketInstance.disconnect();
        socketManager.socket = null;
        socketManager.token = null;
        // Detect current locale from URL path
        const locale = window.location.pathname.match(/^\/(en|de)/)?.[1] || 'de';
        fetch('/api/auth/sign-out', { method: 'POST' })
          .finally(() => {
            window.location.href = `/${locale}/sign-in`;
          });
      });

      // Defensive: remove any stale listeners (from HMR / re-init) before re-attaching
      socketInstance.off("newMessage");
      socketInstance.on("newMessage", (data: MessageEventData) => {
        // First get the current state to check room data
        const state = store.getState();
        const currentRoomId = state.chat?.selectedRoomId;
        const currentUserId = state.user?.currentUser?.id;

        // Find the room in state that this message belongs to
        const roomId =
          typeof data.message.room === "object"
            ? data.message.room.id
            : data.message.room;

        // Skip processing if we don't have a valid room ID
        if (!roomId) {
          console.warn("[Socket] Received message without valid room ID");
          return;
        }

        const roomData = state.chat?.rooms.find((r) => r.id === roomId);

        // If the message contains room data and we have the room in our state, ensure it's preserved
        if (
          roomData &&
          typeof data.message.room === "object" &&
          data.message.room?.id === roomId
        ) {
          // Update room data with any new information but preserve name
          dispatch(
            updateRoom({
              ...roomData,
              ...data.message.room,
              name: data.message.room.name || roomData.name, // Preserve existing name if new one is empty
              lastActivity: new Date().toISOString(),
            }),
          );
        }

        // Check if this is our own message coming back from server
        const senderId =
          typeof data.message.sender === "object"
            ? data.message.sender.id
            : data.message.sender;

        // Convert the socket message to a proper Message type before adding it to the store
        // We need to ensure the message structure matches the expected Message type
        const messageToAdd: Message = {
          ...data.message,
          // Ensure we have the proper room reference - either use the full room object from state or the one from the message
          room:
            roomData ||
            (typeof data.message.room === "object"
              ? (data.message.room as Room)
              : ({ id: data.message.room } as Room)),
          // Ensure we have the proper sender format
          sender: data.message.sender as unknown as User,
          // Normalize parentMessage from populated object to numeric ID
          parentMessage: data.message.parentMessage
            ? typeof data.message.parentMessage === "object"
              ? (data.message.parentMessage as { id: number }).id
              : data.message.parentMessage
            : null,
          // Set defaults for optional fields if not provided
          messageType: data.message.messageType || "text",
          readBy: data.message.readBy || [],
          createdAt: data.message.createdAt || new Date().toISOString(),
        };

        // Thread replies don't belong in the main chat list, but we still need
        // to bump the unread count for support_bot ticketed threads (that's
        // where TS replies to reporters live). Handle increment inline, then
        // bail out of the main-chat flow.
        if (messageToAdd.parentMessage) {
          if (senderId !== currentUserId) {
            const threadIncomingRoom = typeof data.message.room === 'object'
              ? (data.message.room as { room_kind?: string })
              : null;
            const threadRoomKind =
              threadIncomingRoom?.room_kind ??
              (roomData as { room_kind?: string } | undefined)?.room_kind ??
              null;
            const isTicketedThread = Boolean(data._botSilent);
            const senderIsTsOrBot = Boolean(
              (data.message as unknown as { _fromTsTeam?: boolean })._fromTsTeam,
            );
            if (
              threadRoomKind === 'support_bot' &&
              isTicketedThread &&
              senderIsTsOrBot &&
              currentRoomId !== roomId
            ) {
              dispatch(incrementUnreadCount(roomId));
            }
          }
          return;
        }

        // If this is our own message, we need to replace the optimistic one
        if (senderId === currentUserId) {
          const messages = state.chat?.messages || [];
          // Find the optimistic message (with negative ID)
          const optimisticMsg = messages.find(
            (msg) => msg.id < 0 &&
            msg.content === messageToAdd.content &&
            (typeof msg.room === 'object' ? msg.room.id : msg.room) === roomId
          );

          if (optimisticMsg) {
            // Replace optimistic message: swap out the entire messages array
            // because updateMessage searches by ID and the IDs differ (negative vs real)
            dispatch(setMessages(
              messages.map((msg) => msg.id === optimisticMsg.id ? messageToAdd : msg)
            ));
          } else {
            // No optimistic message found, just add the new message
            dispatch(addMessage(messageToAdd));
          }
        } else {
          // Message from another user, just add it
          dispatch(addMessage(messageToAdd));
        }

        // Handle unread counts - reuse senderId from above

        // We already have a valid roomId from earlier check (line 351-360)
        if (senderId !== currentUserId) {
          // Support_bot rooms: server is authoritative for mark-read (see same
          // rationale as in the "connect" handler above). Preemptive client
          // mark-read here would race with the server's pushUnreadCountsToUser
          // and flash the reporter's badge back to 0 a second after it appears.
          const incomingRoomForMark = typeof data.message.room === 'object'
            ? (data.message.room as { room_kind?: string })
            : null;
          const markRoomKind =
            incomingRoomForMark?.room_kind ??
            (roomData as { room_kind?: string } | undefined)?.room_kind ??
            null;
          const isSupportBotRoom = markRoomKind === 'support_bot';
          if (currentRoomId === roomId && !isSupportBotRoom) {
            // Message is in the current room - mark it as read after a delay
            setTimeout(() => {
              if (currentUserId) {
                markRoomMessagesAsRead(roomId, currentUserId);
              }
            }, 1000);
          } else {
            // Read the kind from the incoming payload first since the room may
            // not be hydrated in state.chat.rooms yet; fall back to redux state.
            const incomingRoom = typeof data.message.room === 'object'
              ? (data.message.room as { room_kind?: string })
              : null;
            const roomKind =
              incomingRoom?.room_kind ??
              (roomData as { room_kind?: string } | undefined)?.room_kind ??
              null;
            if (roomKind === 'support_bot') {
              // Support_bot unread is client-driven: too many server-side
              // mark-read paths race with the debounced server push and zero
              // out the badge before the reporter ever sees it. We bump here
              // on every incoming TS/bot message in a ticketed thread that
              // the user isn't currently viewing.
              const isTicketedThread = Boolean(data._botSilent);
              const senderIsTsOrBot = Boolean((data.message as unknown as { _fromTsTeam?: boolean })._fromTsTeam);
              if (isTicketedThread && senderIsTsOrBot) {
                dispatch(incrementUnreadCount(roomId));
              }
            } else if (roomKind) {
              dispatch(incrementUnreadCount(roomId));
            }

            // Create a notification for messages from other rooms
            const senderId = typeof data.message.sender === "object" ? data.message.sender.id : data.message.sender;
            const senderUsername = typeof data.message.sender === "object" ? data.message.sender.username : undefined;
            const senderName = displayNamesRef.current.get(senderId, senderUsername) || "Unknown";

            const roomName = roomData?.name || `Room ${roomId}`;

            handleChatNotification({
              type: 'new_message',
              title: `New message in ${roomName}`,
              message: `${senderName}: ${data.message.content.substring(0, 100)}`,
              roomId: roomId,
              senderId: senderId,
              timestamp: new Date(),
            });
          }
        }
      });

      socketInstance.on("messagesRead", (data: MessageReadEventData) => {
        // Update messages in Redux to reflect read status
        dispatch(markMessagesAsRead(data));

        // Update unread count for the room
        if (data.roomId) {
          const state = store.getState();
          const currentUserId = state.user?.currentUser?.id;
          if (data.userId === currentUserId) {
            dispatch(clearUnreadCount(data.roomId));
          }
        }
      });

      socketInstance.on("userTyping", (data: TypingEventData) => {
        const key = `${data.roomId}-${data.userId}`;

        // Clear any existing timeout for this user's typing indicator
        if (activeTypingRef.current[key]) {
          clearTimeout(activeTypingRef.current[key]);
        }

        // Set typing status
        dispatch(
          setTypingUser({
            roomId: data.roomId,
            userId: data.userId,
            typing: data.typing,
          }),
        );

        // Auto-expire typing indicator after 3 seconds if typing is true
        if (data.typing) {
          activeTypingRef.current[key] = setTimeout(() => {
            dispatch(
              setTypingUser({
                roomId: data.roomId,
                userId: data.userId,
                typing: false,
              }),
            );
            delete activeTypingRef.current[key];
          }, 3000);
        } else {
          // If typing is false, remove from active typing
          delete activeTypingRef.current[key];
        }
      });

      socketInstance.on("roomCreated", (room: Room) => {
        // CRITICAL: Filter rooms to only include ones where current user is a member
        const state = store.getState();
        const currentUserId = state.user?.currentUser?.id;

        if (!currentUserId) {
          console.error("[Socket/roomCreated] Cannot filter room - no current user ID");
          return;
        }

        // Membership is the sole access criterion (enforced server-side too).
        if (!isRoomMember(room, currentUserId)) {
          console.warn(
            `[Socket/SECURITY] Room ${room.id} "${room.name}" created but user ${currentUserId} is NOT a member! Ignoring.`
          );
          return;
        }

        console.log(`[Socket/roomCreated] Adding room ${room.id} "${room.name}" for user ${currentUserId}`);
        dispatch(addRoom(room));
      });

      socketInstance.on("roomUpdated", (room: Room) => {
        // CRITICAL: Filter rooms to only include ones where current user is a member
        const state = store.getState();
        const currentUserId = state.user?.currentUser?.id;

        if (!currentUserId) {
          console.error("[Socket/roomUpdated] Cannot filter room - no current user ID");
          return;
        }

        // Membership is the sole access criterion (enforced server-side too).
        if (!isRoomMember(room, currentUserId)) {
          console.warn(
            `[Socket/SECURITY] Room ${room.id} "${room.name}" updated but user ${currentUserId} is NOT a member! Ignoring.`
          );
          return;
        }

        console.log(`[Socket/roomUpdated] Updating room ${room.id} "${room.name}" for user ${currentUserId}`);
        dispatch(updateRoom(room));
      });

      socketInstance.on("roomList", (rooms: Room[]) => {
        // CRITICAL: Filter rooms to only include ones where current user is a member
        // The backend should already be filtering, but this is a safety check
        const state = store.getState();
        const currentUserId = state.user?.currentUser?.id;

        if (!currentUserId) {
          console.error("[Socket] Cannot filter rooms - no current user ID");
          dispatch(setRooms(rooms));
          return;
        }

        const filteredRooms = rooms.filter((room: Room) => {
          // Membership is the sole access criterion (enforced server-side too).
          const hasAccess = isRoomMember(room, currentUserId);

          if (!hasAccess) {
            console.warn(
              `[Socket/SECURITY] Room ${room.id} "${room.name}" received via socket but user ${currentUserId} is NOT a member!`
            );
          }

          return hasAccess;
        });

        console.log(
          `[Socket/roomList] Received ${rooms.length} rooms, filtered to ${filteredRooms.length} for user ${currentUserId}`
        );

        dispatch(setRooms(filteredRooms));
      });

      socketInstance.on("unreadCounts", (data: UnreadCountData[]) => {
        // First push on this connection = trust verbatim (handshake / reconnect).
        // Subsequent pushes go through the ignore-zero + grace-period rules.
        if (!socketManager.initialUnreadCountsReceived) {
          socketManager.initialUnreadCountsReceived = true;
          dispatch(setUnreadCountsTrusted(data));
        } else {
          dispatch(setUnreadCounts(data));
        }
      });

      socketInstance.on(
        "messageUpdated",
        (payload: { messageId: number; content?: string; content_en?: string }) => {
          dispatch(patchMessage(payload));
        },
      );

      socketInstance.on("messageReaction", (data: ReactionEventData) => {
        dispatch(
          updateMessageReaction({
            messageId: data.messageId,
            reactions: data.reactions,
          }),
        );
      });

      socketInstance.on("messagePinned", (data: PinEventData) => {
        dispatch(toggleMessagePin(data));
      });

      // Slack-style "marked unread" flag — cross-tab sync events.
      socketInstance.on("roomMarkedUnread", ({ roomId }: { roomId: number }) => {
        dispatch(setRoomManuallyMarkedUnread({ roomId, value: true }));
      });
      socketInstance.on("roomUnreadCleared", ({ roomId }: { roomId: number }) => {
        dispatch(setRoomManuallyMarkedUnread({ roomId, value: false }));
      });

      socketInstance.on("roomDeleted", ({ id }: { id: number }) => {
        dispatch(removeRoom(id));
      });

      // Membership changes for THIS user. Without these the room list only
      // catches up on a full reload: a removed user keeps a stale room whose
      // every request now 403s, and an added user doesn't see the room at all.
      // `roomLeft` also fires when the last member is removed and the backend
      // deletes the room outright.
      socketInstance.on("roomLeft", ({ id }: { id: number | string }) => {
        dispatch(removeRoom(Number(id)));
      });

      socketInstance.on("roomJoined", (room: Room) => {
        dispatch(addRoom(room));
      });

      socketInstance.on("newThreadReply", (data: ThreadReplyEventData) => {
        dispatch(
          updateMessageReplyCount({
            messageId: data.parentMessageId,
            replyCount: data.replyCount,
            threadParticipants: data.threadParticipants,
          }),
        );

        // If we have this thread open, add the reply
        const state = store.getState();
        if (state.chat?.activeThreadId === data.parentMessageId) {
          dispatch(addThreadMessage(data.message));
        } else {
          // Thread not open → track as unread
          const roomId = typeof data.message.room === 'object'
            ? (data.message.room as { id: number }).id
            : (data.message.room as number);
          dispatch(addUnreadThread({
            parentId: data.parentMessageId,
            text: data.message.content || '',
            roomId: roomId || 0,
          }));
        }
      });

      return socketInstance;
    }

    return socketManager.socket;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeAgencyIdSelected is an intentional extra dep: an agency switch must recreate the socket (the server freezes socket.user.agency at connect time), so its identity change drives the lifecycle effect
  }, [
    token,
    STRAPI_URL,
    dispatch,
    store,
    markRoomMessagesAsRead,
    handleChatNotification,
    activeAgencyIdSelected,
  ]);

  // Effect to manage socket lifecycle
  useEffect(() => {
    // Increment reference count
    socketManager.refCount++;

    // Initialize or update socket
    initializeSocket();

    // Cleanup function
    const cleanup = () => {
      // Decrement reference count
      socketManager.refCount--;

      // Only disconnect if this was the last hook instance
      if (socketManager.refCount === 0 && socketManager.socket) {
        console.log("[Socket] Last hook unmounting, disconnecting socket");
        socketManager.socket.removeAllListeners();
        socketManager.socket.disconnect();
        socketManager.socket = null;
        socketManager.token = null;
        socketManager.eventsBound = false;
      }

      // Clear all timeouts
      Object.values(activeTypingRef.current).forEach(clearTimeout);
      activeTypingRef.current = {};

      if (pendingMarkRequestRef.current) {
        clearTimeout(pendingMarkRequestRef.current);
        processMarkedMessages(); // Process any pending messages before cleanup
      }
    };

    cleanupRef.current = cleanup;

    return cleanup;
  }, [token, initializeSocket, processMarkedMessages]);

  // Return the socket instance and utility functions
  return {
    socket: socketManager.socket,
    isConnected,
    sendMessage,
    joinRoom,
    leaveRoom,
    emitTypingEvent,
    markRoomMessagesAsRead,
    getUnreadCounts,
    notifications,
  };
};

/**
 * Force-disconnect the chat socket singleton. Call this before a hard
 * navigation (agency switch, logout, login) so the server removes the
 * socket immediately instead of waiting ~25s for the ping-timeout — avoids
 * zombie sockets pushing stale unreadCounts during that window.
 */
export function disconnectChatSocket() {
  if (socketManager.socket) {
    try {
      socketManager.socket.removeAllListeners();
      socketManager.socket.disconnect();
    } catch {}
    socketManager.socket = null;
    if (typeof window !== "undefined") {
      (window as unknown as { __socket: Socket | null }).__socket = null;
    }
    socketManager.token = null;
    socketManager.eventsBound = false;
    socketManager.refCount = 0;
  }
}